// Bilingual (English + Arabic) content for the public MNAH website
// Admin dashboard remains in English (internal operations tool).

const dictionary = {
  en: {
    dir: "ltr",
    code: "en",
    label: "EN",
    other: { code: "ar", label: "عربي" },

    nav: {
      home: "Home",
      company: "Company",
      services: "Services",
      projects: "Projects",
      gallery: "Gallery",
      branches: "Branches",
      careers: "Join Us",
      suppliers: "Supplier Portal",
      contact: "Contact",
      adminLogin: "Admin Login",
      requestService: "Request Service",
      // legacy keys still used inside sections / footer
      about: "About",
      leadership: "Leadership",
      workforce: "Workforce",
      clients: "Clients",
      // Mega-menu submenus
      companyMenu: {
        aboutSelf: "About El-Self",
        fields: "Fields of Business",
        hse: "Health, Safety & Environment",
        clients: "Clients",
        partners: "Partners",
        leadership: "Leadership",
        workforce: "Workforce",
      },
      servicesMenu: {
        procurement: "Procurement",
        devFinance: "Development, Investment & Finance",
        engineering: "Engineering",
        epc: "Integrated EPC",
        contracting: "Contracting",
        electroMechanical: "Electro Mechanical",
      },
    },

    topbar: {
      crLabel: "C.R.",
      crNumber: "1116004323",
      registered: "Registered in the Kingdom of Saudi Arabia",
    },

    hero: {
      slides: [
        {
          eyebrow: "Saudi Arabia · Established 2008",
          title: ["Engineering", "the Kingdom's", "skyline."],
          subtitle:
            "Heavy contracting, large-scale cleaning, technical maintenance and airport ground services — delivered by 1,000+ skilled professionals.",
        },
        {
          eyebrow: "Airport Ground Operations",
          title: ["Precision", "on the", "tarmac."],
          subtitle:
            "GSE handling, aircraft turnaround support and 24/7 ground operations across KSA's busiest airports.",
        },
        {
          eyebrow: "Heavy Contracting",
          title: ["Built to", "withstand", "scale."],
          subtitle:
            "Civil works, MEP, fit-out and turnkey contracting for Aramco, NEOM, PIF and the Kingdom's largest developers.",
        },
      ],
      ctaPrimary: "Request a Service",
      ctaSecondary: "Explore Projects",
    },

    about: {
      eyebrow: "/ About the company",
      heading1: "A force of",
      heading1Accent: "a thousand",
      heading2: ", with the precision of one.",
      paragraph1Strong: "Mohammed Naser Al Hammadi Contracting Company",
      paragraph1:
        " is a Saudi-headquartered enterprise delivering large-scale contracting, industrial cleaning, technical maintenance and airport ground services across the Kingdom.",
      paragraph2:
        "From PIF developments in Riyadh to ground operations at King Abdulaziz International, our 1,000+ workforce operates 24/7 — combining Vision 2030 ambition with the discipline of legacy Saudi craftsmanship.",
      stats: [
        { label: "Skilled Employees", description: "Engineers, technicians, operators" },
        { label: "Years of Operation", description: "Serving the Kingdom since 2008" },
        { label: "Projects Delivered", description: "Across all four service lines" },
        { label: "Service Verticals", description: "Contracting · Cleaning · Maintenance · Airport" },
      ],
    },

    leadership: {
      eyebrow: "/ Leadership",
      heading1: "A message from",
      heading1Accent: "our Chief Executive.",
      quote:
        "At MNH Contracting, every project we deliver — whether a tower in Riyadh, a refinery in Dammam, or an aircraft turnaround in Jeddah — carries the trust of the Kingdom. Our 1,000+ professionals are the quiet force behind Saudi Arabia's most important operations, and we are proud to serve Vision 2030 with discipline, precision, and Saudi pride.",
      role: "Chief Executive Officer",
      name: "Mohammed Ali",
      companyLine: "MNH Contracting Co. · KSA",
      est: "Est. 2008",
      facts: [
        { label: "Leadership", value: "Since 2008" },
        { label: "Focus", value: "Vision 2030" },
        { label: "Workforce", value: "1,000+ strong" },
      ],
    },

    workforce: {
      eyebrow: "/ Our Workforce",
      heading1: "One thousand strong.",
      heading1Accent: "One operating standard.",
      lead:
        "MNH operates with one of the largest, most disciplined contracting workforces in the Western Region — drawn from across the Kingdom, the GCC, and three international recruitment hubs in South Asia.",
      paragraph1:
        "Every employee — from civil engineer to ramp agent — wears MNH-branded high-visibility uniforms, completes mandatory HSE induction, and follows a single accountability framework. We deploy crews to PIF, Aramco, GACA and NEOM sites with full mobilisation capability — visa, transport, accommodation and PPE — within 72 hours.",
      paragraph2:
        "We are aligned with Vision 2030's Saudization roadmap, with active Nitaqat-platinum recruitment for Saudi nationals across engineering, supervision and operations roles.",
      photoCaption: "MNH Operations Team · Jeddah Headquarters",
      pillars: [
        {
          title: "Safety First",
          description:
            "100% PPE compliance, daily toolbox talks, NEBOSH-led HSE programme.",
          tag: "HSE",
        },
        {
          title: "Multi-National",
          description:
            "Saudi, Egyptian, Indian, Pakistani, Filipino, Bangladeshi & Nepali — one team, one standard.",
          tag: "Diversity",
        },
        {
          title: "Certified & Trained",
          description:
            "OSHA, NEBOSH, IOSH, IPAF and trade-specific certifications across the workforce.",
          tag: "Capability",
        },
        {
          title: "Vision 2030 Aligned",
          description:
            "Active Saudization in engineering, supervision and operational leadership roles.",
          tag: "Saudization",
        },
      ],
      stats: [
        { value: "1,000+", label: "Active Workforce" },
        { value: "72 hrs", label: "Mobilisation SLA" },
        { value: "24/7", label: "Operations Coverage" },
        { value: "0", label: "Lost-Time Incidents (last 6 months)" },
      ],
      galleryEyebrow: "/ Life at MNH",
      galleryTitle: "More than a job. A team.",
      galleryDescription:
        "Engagement, training, team-building and Vision 2030 alignment — the culture behind the uniform.",
      galleryCaptions: [
        { caption: "Engagement & Wellbeing", tag: "Culture" },
        { caption: "One Team, One Mission", tag: "Collaboration" },
        { caption: "Daily Coordination", tag: "Operations Meet" },
        { caption: "Vision 2030 Aligned", tag: "Saudi Future" },
        { caption: "Off-site Team Building", tag: "Engagement" },
      ],
    },

    workforceAvailable: {
      eyebrow: "/ Workforce on Demand",
      heading1: "1,000+ skilled workers,",
      heading1Accent: "available now.",
      lead:
        "MNH operates one of the largest pre-trained, document-ready contracting workforces in the Kingdom — deployable to your site within 72 hours, fully managed end-to-end.",
      slaTitle: "72-hour Mobilisation",
      slaText: "Visa, transport, accommodation, PPE, uniforms and supervision included. One contact, one invoice, one accountable contractor.",
      includes: [
        { label: "Iqama & visa management", icon: "shield" },
        { label: "Transport + accommodation", icon: "truck" },
        { label: "PPE + MNH uniforms", icon: "hardhat" },
        { label: "On-site supervision", icon: "users" },
        { label: "HSE-certified crews", icon: "check" },
        { label: "Replacement guarantee", icon: "refresh" },
      ],
      tradesTitle: "Available Trades",
      tradesSubtitle: "Pre-screened, certified, ready-to-deploy crews across every contracting discipline.",
      trades: [
        { name: "Civil & Steel Workers", count: "220+", desc: "Masons, carpenters, steel fixers, concrete pourers and finishers." },
        { name: "MEP Technicians", count: "180+", desc: "HVAC, electrical, plumbing, fire-fighting, LV/ELV technicians." },
        { name: "Industrial Cleaners", count: "200+", desc: "Refinery, plant, factory and high-rise cleaning crews." },
        { name: "Airport Ground Staff", count: "120+", desc: "Cabin cleaning, ramp ops, GSE drivers, cargo handlers." },
        { name: "Landscape & Horticulture", count: "90+", desc: "Hardscape, irrigation, planting and ongoing horticulture." },
        { name: "Facility & Maintenance", count: "110+", desc: "Janitorial, pest control, MEP maintenance, helpdesk teams." },
        { name: "Heavy Equipment Ops", count: "60+", desc: "Crane, forklift, loader, bobcat and lowbed operators." },
        { name: "Supervision & QA/QC", count: "40+", desc: "Site engineers, supervisors, safety officers, QC inspectors." },
      ],
      cta: "Request Workforce",
      ctaSecondary: "Talk to Operations",
      callTitle: "Direct line · Mobilisation desk",
    },


    services: {
      eyebrow: "/ What we do",
      heading1: "Seven service lines.",
      heading1Accent: "One operating standard.",
      description:
        "Whether we're pouring concrete in Dammam, scrubbing a refinery floor in Yanbu or pushing back an A380 in Jeddah — every project runs on the same SOPs, HSE protocols and accountability framework.",
      cards: [
        {
          name: "General Contracting",
          summary:
            "Civil works, MEP integration, fit-out and turnkey delivery for commercial, industrial and government developments.",
          capabilities: ["Civil & Structural", "MEP", "Interior Fit-out", "Turnkey Build"],
        },
        {
          name: "Construction",
          summary:
            "Ground-up construction for towers, villas, industrial plants and public infrastructure — executed with Saudi-grade quality and safety.",
          capabilities: ["Concrete & Steel", "High-Rise", "Villas & Housing", "Public Infrastructure"],
        },
        {
          name: "Electro Mechanical",
          summary:
            "End-to-end MEP design, installation and commissioning — HVAC, power systems, fire fighting, plumbing and smart building integration.",
          capabilities: ["HVAC Systems", "Power & LV", "Fire Fighting", "Smart Building"],
        },
        {
          name: "Landscaping",
          summary:
            "Hard & soft landscaping for compounds, parks and corporate campuses — irrigation, hardscape, planting and ongoing horticulture care.",
          capabilities: ["Hardscape", "Irrigation", "Planting", "Horticulture Care"],
        },
        {
          name: "Cleaning Services",
          summary:
            "Industrial, commercial and facility-grade cleaning — from malls and hospitals to refineries and labour camps.",
          capabilities: ["Daily Janitorial", "Industrial Deep Clean", "Façade Cleaning", "Sanitisation"],
        },
        {
          name: "Maintenance",
          summary:
            "Preventive and reactive maintenance for HVAC, electrical, plumbing and full FM contracts across KSA.",
          capabilities: ["HVAC", "Electrical", "Plumbing", "Integrated FM"],
        },
        {
          name: "Airport Ground Services",
          summary:
            "GSE handling, aircraft turnaround, baggage and ramp services across the Kingdom's busiest international gateways.",
          capabilities: ["Ramp Services", "GSE Operations", "Baggage Handling", "Aircraft Turnaround"],
        },
      ],
    },

    projects: {
      eyebrow: "/ Selected work",
      heading1: "Projects across",
      heading1Accent: "the Kingdom.",
      description:
        "From PIF headquarters fit-outs to ground operations at major airports — a snapshot of what our teams are delivering this year.",
      featured: "Featured",
      loading: "Project portfolio loading…",
    },

    emGallery: {
      eyebrow: "/ Electro Mechanical Showcase",
      heading1: "Power. HVAC. Fire. Smart systems.",
      heading1Accent: "Engineered, installed, commissioned.",
      description:
        "From low-voltage distribution rooms to chiller plants and intelligent building controls — a glimpse inside our MEP delivery across the Kingdom.",
      photos: [
        { caption: "HVAC Plant Rooms", tag: "Cooling & Ventilation" },
        { caption: "Power Distribution", tag: "MV / LV Switchgear" },
        { caption: "Control & Automation", tag: "BMS / SCADA" },
        { caption: "Fire Fighting Systems", tag: "Sprinkler & Alarm" },
        { caption: "Cable Management", tag: "Tray & Containment" },
        { caption: "Lighting & Power", tag: "Final Connections" },
      ],
    },

    clients: {
      eyebrow: "/ Clients & Partners",
      heading1: "Trusted by leaders",
      heading1Accent: "across the Kingdom.",
      description:
        "From Saudi banking and aviation to global industrial brands — MNH teams operate inside the premises of the region's most demanding organisations.",
      filterAll: "All Sectors",
      filterEnergy: "Energy",
      filterAviation: "Aviation",
      filterGovernment: "Government",
      filterFinance: "Finance",
      filterIndustrial: "Industrial",
      filterMega: "Giga Projects",
      labelSector: "Sector",
      labelSince: "Engaged Since",
      labelLocation: "Location",
      labelScope: "Scope of Work",
      labelStatus: "Engagement",
      statusActive: "Active",
      statusCompleted: "Completed",
      cta: "View Engagement Details",
      closeLabel: "Close",
      stats: [
        { value: "120+", label: "Active client accounts" },
        { value: "17", label: "Years of partnerships" },
        { value: "98%", label: "Client retention rate" },
        { value: "11", label: "Sectors served" },
      ],
      directory: [
        {
          id: "aramco",
          name: "Saudi Aramco",
          mark: "ARC",
          sector: "energy",
          since: "2011",
          location: "Dhahran · Eastern Province",
          status: "active",
          summary:
            "Industrial cleaning, technical maintenance and shutdown support across multiple Aramco facilities and residential compounds.",
          scope: ["Industrial cleaning", "MEP maintenance", "Manpower supply", "Compound services"],
          accent: "#0E7C66",
        },
        {
          id: "sabic",
          name: "SABIC",
          mark: "SBC",
          sector: "industrial",
          since: "2013",
          location: "Jubail Industrial City",
          status: "active",
          summary:
            "Petrochemical plant cleaning, insulation work and contract manpower for routine and turnaround operations.",
          scope: ["Plant cleaning", "Insulation", "Skilled manpower", "Scaffolding"],
          accent: "#1E5BA8",
        },
        {
          id: "neom",
          name: "NEOM",
          mark: "NEO",
          sector: "mega",
          since: "2021",
          location: "Tabuk Region",
          status: "active",
          summary:
            "Site logistics, civil works support and large-scale workforce deployment across THE LINE and Trojena construction zones.",
          scope: ["Civil works", "Site logistics", "Workforce camps", "Heavy cleaning"],
          accent: "#000000",
        },
        {
          id: "rsg",
          name: "Red Sea Global",
          mark: "RSG",
          sector: "mega",
          since: "2022",
          location: "Umluj · Tabuk",
          status: "active",
          summary:
            "Hospitality fit-out support, landscaping and continuous facility maintenance for resort island developments.",
          scope: ["Landscaping", "Fit-out", "Facility maintenance", "Cleaning"],
          accent: "#B47A3F",
        },
        {
          id: "pif",
          name: "Public Investment Fund",
          mark: "PIF",
          sector: "government",
          since: "2019",
          location: "Riyadh",
          status: "active",
          summary:
            "Approved contractor for multiple PIF subsidiary developments — delivering MEP, finishing and grounds operations.",
          scope: ["MEP works", "Finishing", "Grounds keeping", "Logistics"],
          accent: "#0B6E4F",
        },
        {
          id: "saudia",
          name: "Saudia Airlines",
          mark: "SV",
          sector: "aviation",
          since: "2014",
          location: "Jeddah · KAIA",
          status: "active",
          summary:
            "Aircraft cabin cleaning, ground support equipment handling and 24/7 ramp operations at King Abdulaziz International Airport.",
          scope: ["Cabin cleaning", "GSE handling", "Ramp ops", "Cargo support"],
          accent: "#0E5494",
        },
        {
          id: "kaia",
          name: "King Abdulaziz Int'l Airport",
          mark: "JED",
          sector: "aviation",
          since: "2012",
          location: "Jeddah",
          status: "active",
          summary:
            "Terminal deep cleaning, technical maintenance and façade works across Hajj and main passenger terminals.",
          scope: ["Terminal cleaning", "Façade works", "MEP maintenance", "Pest control"],
          accent: "#0A3D62",
        },
        {
          id: "rcjy",
          name: "Royal Commission · Jubail",
          mark: "RCJY",
          sector: "government",
          since: "2010",
          location: "Jubail Industrial City",
          status: "active",
          summary:
            "City-scale landscaping, public area cleaning and infrastructure maintenance under Royal Commission framework contracts.",
          scope: ["Landscaping", "Public cleaning", "Maintenance", "Pest control"],
          accent: "#2E7D32",
        },
        {
          id: "alrajhi",
          name: "Al Rajhi Bank",
          mark: "AR",
          sector: "finance",
          since: "2015",
          location: "Riyadh · Nationwide",
          status: "active",
          summary:
            "Branch fit-out, signage installation and facility services across more than 200 branch locations across the Kingdom.",
          scope: ["Branch fit-out", "Signage", "Facility services", "Maintenance"],
          accent: "#0E6D3F",
        },
        {
          id: "snb",
          name: "Saudi National Bank",
          mark: "SNB",
          sector: "finance",
          since: "2017",
          location: "Riyadh · Nationwide",
          status: "active",
          summary:
            "HQ tower technical maintenance, branch refurbishment and ATM enclosure works on rolling annual contracts.",
          scope: ["HQ maintenance", "Refurbishment", "ATM enclosures", "Cleaning"],
          accent: "#0B5394",
        },
        {
          id: "maaden",
          name: "Ma'aden",
          mark: "MDN",
          sector: "industrial",
          since: "2016",
          location: "Ras Al-Khair · Eastern Province",
          status: "active",
          summary:
            "Mining and smelter cleaning support, manpower supply and insulation services across the Ras Al-Khair complex.",
          scope: ["Smelter cleaning", "Insulation", "Manpower", "Scaffolding"],
          accent: "#7B341E",
        },
        {
          id: "mod",
          name: "Ministry of Defense",
          mark: "MOD",
          sector: "government",
          since: "2013",
          location: "Riyadh · KSA",
          status: "active",
          summary:
            "Cleared facility maintenance, grounds keeping and discreet manpower supply across designated MOD compounds.",
          scope: ["Facility services", "Grounds keeping", "Manpower", "Pest control"],
          accent: "#3B3F3A",
        },
      ],
    },

    milestones: {
      eyebrow: "/ Our Journey",
      heading1: "Seventeen years.",
      heading1Accent: "One trajectory.",
      description:
        "From a single contracting license in Jeddah to a multi-branch operation serving the Kingdom's most important developments.",
      items: [
        { year: "2008", title: "Founded in Jeddah", text: "MNH Contracting Co. registered under C.R. 1116004323 with a focus on general contracting." },
        { year: "2011", title: "First Aramco engagement", text: "Awarded the first framework contract with Saudi Aramco for industrial cleaning and manpower." },
        { year: "2013", title: "Eastern Province expansion", text: "Opened the Dammam branch to serve Jubail industrial clients including SABIC." },
        { year: "2016", title: "1,000-strong workforce", text: "Crossed 1,000 active personnel across civil, MEP, cleaning and airport operations." },
        { year: "2019", title: "PIF approved contractor", text: "Listed as approved contractor across multiple Public Investment Fund subsidiaries." },
        { year: "2021", title: "NEOM mobilisation", text: "Deployed first crews to THE LINE and Trojena construction zones in Tabuk." },
        { year: "2023", title: "Eleven locations", text: "Operating from eight KSA branches and three international sourcing offices." },
        { year: "2026", title: "Vision 2030 partner", text: "Aligned with Saudization roadmap; Nitaqat-platinum active recruitment in engineering and supervision." },
      ],
    },

    certifications: {
      eyebrow: "/ Certified Operations",
      heading1: "Audited, accredited,",
      heading1Accent: "approved.",
      description:
        "MNH operates inside the Kingdom's most regulated environments — refineries, airports, ministries, banking towers. Every certification we hold is a license to work where it matters.",
      items: [
        { code: "ISO 9001", title: "Quality Management" },
        { code: "ISO 14001", title: "Environmental Management" },
        { code: "ISO 45001", title: "Occupational Health & Safety" },
        { code: "NEBOSH", title: "HSE Programme" },
        { code: "OSHA", title: "Worksite Safety" },
        { code: "Saudization", title: "Nitaqat Platinum" },
        { code: "Aramco", title: "Approved Contractor" },
        { code: "GACA", title: "Airport Operations" },
      ],
    },

    vision2030: {
      eyebrow: "/ Vision 2030",
      heading1: "Building the future",
      heading1Accent: "the Kingdom imagined.",
      description:
        "MNH contributes directly to four Vision 2030 pillars — infrastructure, Saudization, public-sector services and giga-project delivery. Our operations on NEOM, Red Sea, PIF developments and Aramco facilities make us an active enabler, not a passive vendor.",
      pillars: [
        { tag: "Infrastructure", title: "Giga-project delivery", text: "Civil works, MEP and large-scale workforce camps for NEOM, Red Sea Global and PIF developments." },
        { tag: "Saudization", title: "Nitaqat Platinum", text: "Active Saudi-national recruitment across engineering, supervision and operations." },
        { tag: "Public Services", title: "Government contracts", text: "Long-running framework agreements with Royal Commission, MOD and ministerial clients." },
        { tag: "Industry", title: "Energy & industrial", text: "Plant cleaning, shutdown support and maintenance across Aramco, SABIC and Ma'aden." },
      ],
      cta: "Engage MNH",
    },

    testimonials: {
      eyebrow: "/ What Clients Say",
      heading1: "Trusted on the ground.",
      heading1Accent: "Recommended at the top.",
      description:
        "Selected feedback from operations heads, facility directors and procurement officers across the Kingdom.",
      items: [
        { quote: "MNH crews mobilised on our shutdown window in under 72 hours. The discipline on site is exactly what an Aramco facility needs.", name: "Facility Director", company: "Energy sector · Eastern Province" },
        { quote: "Their workforce shows up in uniform, on time, with PPE. That alone puts them ahead of most contractors we audit.", name: "Procurement Head", company: "Petrochemicals · Jubail" },
        { quote: "We've run MNH on three branch refurbishment cycles. Predictable, clean handover, zero punch-list slippage.", name: "Facilities Manager", company: "Banking · Riyadh" },
        { quote: "Airport ground operations demand precision. MNH ramp crews integrate with our SOPs without friction.", name: "Operations Manager", company: "Aviation · Jeddah" },
        { quote: "What I value most is consistency — same standard in Tabuk as in Jubail. That's rare in this market.", name: "Project Director", company: "Giga-project · Tabuk" },
      ],
    },


    branches: {
      eyebrow: "/ Our Presence",
      heading1: "One company.",
      heading1Accent: "Eleven locations.",
      description:
        "Eight operational branches across the Kingdom and three international sourcing offices — giving MNH the reach, workforce pipeline and rapid deployment capability that 1,000+ active contracts demand.",
      ksaTitle: "Kingdom Network",
      ksaSubtitle: "Operational branches across Saudi Arabia",
      intlTitle: "International Offices",
      intlSubtitle: "Recruitment & workforce sourcing partners",
      labelHQ: "Head Branch",
      labelBranch: "Regional Branch",
      labelIntl: "Sourcing Office",
      ksa: [
        { city: "Jeddah", region: "Makkah Region", role: "headquarters", note: "Group Headquarters" },
        { city: "Riyadh", region: "Riyadh Region", role: "branch", note: "Central operations & government accounts" },
        { city: "Dammam", region: "Eastern Province", role: "branch", note: "Industrial & oil-sector projects" },
        { city: "Medina", region: "Madinah Region", role: "branch", note: "Facility & religious-sites servicing" },
        { city: "Abha", region: "Asir Region", role: "branch", note: "Southern highlands operations" },
        { city: "Buraydah", region: "Al-Qassim Region", role: "branch", note: "Central Kingdom coverage" },
        { city: "Tabuk", region: "Tabuk Region", role: "branch", note: "NEOM & north-west projects" },
        { city: "Hail", region: "Hail Region", role: "branch", note: "Northern logistics hub" },
        { city: "Jazan", region: "Jazan Region", role: "branch", note: "Red Sea southern gateway" },
      ],
      intl: [
        { country: "Bangladesh", city: "Dhaka", note: "Skilled-labour sourcing & pre-deployment training" },
        { country: "India", city: "New Delhi", note: "Technical workforce recruitment & mobilisation" },
        { country: "Nepal", city: "Kathmandu", note: "Certified labour pipeline & medical clearance" },
      ],
    },

    contact: {
      eyebrow: "/ Engage MNH",
      heading1: "Request a service.",
      heading1Accent: "Get a callback.",
      description:
        "Tell us about your site, scope and timeline. A senior account manager will respond within one business day with a tailored proposal.",
      addressTitle: "Registered Office",
      addressLine1: "King Fahd Dist., Prince Mashhour Bin Abdulaziz St.,",
      addressLine2: "Bldg 5207, Ad Duwadimi 17452, Kingdom of Saudi Arabia",
      phoneTitle: "Phone",
      emailTitle: "Email",
      websiteTitle: "Website",
      labelName: "Full Name",
      labelEmail: "Email",
      labelPhone: "Phone",
      labelService: "Service Required",
      labelMessage: "Project Brief",
      placeholderName: "Mohammed Al-Hammadi",
      placeholderEmail: "you@company.com",
      placeholderPhone: "+966 …",
      placeholderMessage: "Scope, location, timeline…",
      selectService: "Select a service",
      serviceOptions: [
        "General Contracting",
        "Construction",
        "Electro Mechanical",
        "Landscaping",
        "Cleaning Services",
        "Maintenance",
        "Airport Ground Services",
      ],
      disclaimer:
        "By submitting, you agree to be contacted by MNH Contracting representatives.",
      submit: "Send Request",
      submitting: "Submitting…",
      toastFill: "Please complete every field before submitting.",
      toastSuccess: "Request submitted. Our team will reach out within 24 hours.",
      toastError: "Could not submit. Try again.",
    },

    footer: {
      tagline:
        "Mohammed Naser Al Hammadi Contracting Company — Saudi-headquartered, Vision 2030 aligned, delivering at scale across contracting, cleaning, maintenance and airport ground services.",
      servicesTitle: "Services",
      companyTitle: "Company",
      reachTitle: "Reach",
      links: {
        about: "About",
        projects: "Projects",
        contact: "Contact",
        admin: "Admin",
      },
      services: ["Contracting", "Construction", "Electro Mechanical", "Landscaping", "Cleaning", "Maintenance", "Airport Ground"],
      addressLine1: "King Fahd Dist., Prince Mashhour Bin Abdulaziz St.,",
      addressLine2: "Bldg 5207, Ad Duwadimi 17452, KSA",
      copyright: "All rights reserved.",
      madeIn: "Made in the Kingdom of Saudi Arabia",
    },

    login: {
      eyebrow: "/ Admin Console",
      heading1: "Sign in to",
      heading1Accent: "MNH Operations.",
      description:
        "Manage projects, inventory, employees and inbound service requests across the Kingdom.",
      labelEmail: "Email",
      labelPassword: "Password",
      submit: "Sign in",
      submitting: "Signing in…",
      demoLabel: "Demo credentials:",
      right: {
        eyebrow: "Vision 2030 · KSA",
        h1: "Built by",
        accent: "a thousand",
        h2: "hands.",
        description:
          "From the runways of Jeddah to the towers of Riyadh — MNH Contracting orchestrates the operations powering Saudi Arabia's largest projects.",
      },
    },

    gallery: {
      eyebrow: "/ Gallery",
      heading1: "The MNH visual archive.",
      heading1Accent: "Workforce. Sites. Systems. Stories.",
      description:
        "Every uniform, project, and team moment — captured across the Kingdom and beyond.",
      backHome: "Back to Home",
      photoCount: "photos",
      filters: {
        all: "All",
        workforce: "Workforce",
        services: "Services",
        em: "Electro Mechanical",
        projects: "Projects",
        culture: "Life at MNH",
      },
    },
  },

  ar: {
    dir: "rtl",
    code: "ar",
    label: "عربي",
    other: { code: "en", label: "EN" },

    nav: {
      home: "الرئيسية",
      company: "الشركة",
      services: "الخدمات",
      projects: "المشاريع",
      gallery: "معرض الصور",
      branches: "الفروع",
      careers: "انضم إلينا",
      suppliers: "بوابة الموردين",
      contact: "تواصل معنا",
      adminLogin: "دخول المسؤول",
      requestService: "اطلب خدمة",
      about: "عن الشركة",
      leadership: "القيادة",
      workforce: "فريق العمل",
      clients: "العملاء",
      companyMenu: {
        aboutSelf: "نبذة عن الشركة",
        fields: "مجالات العمل",
        hse: "الصحة والسلامة والبيئة",
        clients: "العملاء",
        partners: "الشركاء",
        leadership: "القيادة",
        workforce: "فريق العمل",
      },
      servicesMenu: {
        procurement: "المشتريات",
        devFinance: "التطوير والاستثمار والتمويل",
        engineering: "الهندسة",
        epc: "EPC المتكامل",
        contracting: "المقاولات",
        electroMechanical: "كهروميكانيكي",
      },
    },

    topbar: {
      crLabel: "س.ت.",
      crNumber: "1116004323",
      registered: "مسجلة في المملكة العربية السعودية",
    },

    hero: {
      slides: [
        {
          eyebrow: "المملكة العربية السعودية · تأسست ٢٠٠٨",
          title: ["نبني", "أفق", "المملكة."],
          subtitle:
            "مقاولات ثقيلة، تنظيف على نطاق واسع، صيانة تقنية وخدمات أرضية للمطارات — ينفذها أكثر من ١٠٠٠ متخصص ماهر.",
        },
        {
          eyebrow: "العمليات الأرضية للمطارات",
          title: ["الدقة", "على أرض", "المدارج."],
          subtitle:
            "مناولة معدات الطائرات، دعم دوران الطائرات، وعمليات أرضية على مدار الساعة في أكثر مطارات المملكة ازدحاماً.",
        },
        {
          eyebrow: "المقاولات الثقيلة",
          title: ["بُني", "ليصمد", "على نطاق واسع."],
          subtitle:
            "أعمال مدنية، كهروميكانيكية، وتشطيبات وتسليم بنظام المفتاح لأرامكو ونيوم وصندوق الاستثمارات العامة وكبار المطورين في المملكة.",
        },
      ],
      ctaPrimary: "اطلب خدمة",
      ctaSecondary: "استكشف المشاريع",
    },

    about: {
      eyebrow: "/ عن الشركة",
      heading1: "قوةٌ من",
      heading1Accent: "ألف عامل",
      heading2: "، بدقة فردٍ واحد.",
      paragraph1Strong: "شركة محمد ناصر الحمادي للمقاولات",
      paragraph1:
        " هي مؤسسة سعودية المقر، تقدّم خدمات المقاولات الكبرى والتنظيف الصناعي والصيانة التقنية والخدمات الأرضية للمطارات في جميع أنحاء المملكة.",
      paragraph2:
        "من مشاريع صندوق الاستثمارات العامة في الرياض إلى العمليات الأرضية في مطار الملك عبدالعزيز الدولي، تعمل قوتنا العاملة التي تتجاوز ١٠٠٠ موظف على مدار الساعة — جامعةً بين طموح رؤية ٢٠٣٠ وانضباط الحرفية السعودية العريقة.",
      stats: [
        { label: "موظف ماهر", description: "مهندسون، فنيون، مشغلو معدات" },
        { label: "عاماً من العمل", description: "نخدم المملكة منذ ٢٠٠٨" },
        { label: "مشروع منجز", description: "عبر خطوط الخدمة الأربعة" },
        { label: "قطاعات خدمية", description: "مقاولات · تنظيف · صيانة · مطارات" },
      ],
    },

    leadership: {
      eyebrow: "/ القيادة",
      heading1: "كلمة من",
      heading1Accent: "رئيسنا التنفيذي.",
      quote:
        "في شركة محمد ناصر الحمادي للمقاولات، كل مشروع ننفذه — سواء برج في الرياض، أو مصفاة في الدمام، أو خدمة طائرة في جدة — يحمل ثقة المملكة. إن متخصصينا الذين يتجاوز عددهم ١٠٠٠ هم القوة الصامتة خلف أهم العمليات في المملكة العربية السعودية، ونفخر بخدمة رؤية ٢٠٣٠ بانضباط ودقة واعتزاز سعودي.",
      role: "الرئيس التنفيذي",
      name: "محمد علي",
      companyLine: "شركة محمد ناصر الحمادي للمقاولات · المملكة العربية السعودية",
      est: "تأسست ٢٠٠٨",
      facts: [
        { label: "القيادة", value: "منذ ٢٠٠٨" },
        { label: "التوجه", value: "رؤية ٢٠٣٠" },
        { label: "القوى العاملة", value: "أكثر من ١٠٠٠" },
      ],
    },

    workforce: {
      eyebrow: "/ فريق العمل",
      heading1: "ألفُ موظف.",
      heading1Accent: "ومعيارٌ تشغيلي واحد.",
      lead:
        "تعمل الحمادي بأكبر وأكثر القوى العاملة انضباطًا في المنطقة الغربية — من جميع أنحاء المملكة، ودول الخليج، وثلاثة مكاتب توظيف دولية في جنوب آسيا.",
      paragraph1:
        "كل موظف — من المهندس المدني إلى عامل الممرات — يرتدي زي الحمادي عالي الإضاءة، ويكمل تدريب السلامة الإلزامي، ويتبع إطار مساءلة موحدًا. ننشر فرقنا في مواقع صندوق الاستثمارات العامة وأرامكو والهيئة العامة للطيران المدني ونيوم بكامل قدرة الحشد — التأشيرة والنقل والإسكان ومعدات الوقاية — خلال ٧٢ ساعة.",
      paragraph2:
        "نحن متوائمون مع خارطة طريق السعودة في رؤية ٢٠٣٠، مع توظيف فعّال بمستوى نطاقات بلاتينيوم للمواطنين السعوديين في الهندسة والإشراف والعمليات.",
      photoCaption: "فريق عمليات الحمادي · المقر الرئيسي في جدة",
      pillars: [
        {
          title: "السلامة أولاً",
          description:
            "التزام ١٠٠٪ بمعدات الوقاية، اجتماعات سلامة يومية، وبرنامج HSE معتمد من NEBOSH.",
          tag: "HSE",
        },
        {
          title: "تنوع جنسيات",
          description:
            "سعوديون، مصريون، هنود، باكستانيون، فلبينيون، بنغلاديشيون ونيباليون — فريق واحد بمعيار واحد.",
          tag: "تنوع",
        },
        {
          title: "مدرّبون ومعتمدون",
          description:
            "شهادات OSHA و NEBOSH و IOSH و IPAF وشهادات حرفية متخصصة عبر القوى العاملة.",
          tag: "كفاءة",
        },
        {
          title: "متوائم مع رؤية ٢٠٣٠",
          description:
            "سعودة فعّالة في أدوار الهندسة والإشراف والقيادة التشغيلية.",
          tag: "السعودة",
        },
      ],
      stats: [
        { value: "+١٠٠٠", label: "قوى عاملة فعّالة" },
        { value: "٧٢ ساعة", label: "زمن الحشد" },
        { value: "٢٤/٧", label: "تغطية العمليات" },
        { value: "٠", label: "حوادث ضياع وقت (آخر ٦ أشهر)" },
      ],
      galleryEyebrow: "/ الحياة في الحمادي",
      galleryTitle: "أكثر من وظيفة. فريق واحد.",
      galleryDescription:
        "تفاعل، تدريب، بناء فريق، وتوافق مع رؤية ٢٠٣٠ — الثقافة التي تقف خلف الزي الموحد.",
      galleryCaptions: [
        { caption: "التفاعل والرعاية", tag: "ثقافة" },
        { caption: "فريق واحد، رسالة واحدة", tag: "تعاون" },
        { caption: "التنسيق اليومي", tag: "اجتماع العمليات" },
        { caption: "التوافق مع رؤية ٢٠٣٠", tag: "مستقبل المملكة" },
        { caption: "فعاليات بناء الفريق", tag: "تفاعل" },
      ],
    },

    workforceAvailable: {
      eyebrow: "/ قوى عاملة عند الطلب",
      heading1: "أكثر من ١٠٠٠ عامل ماهر،",
      heading1Accent: "جاهزون للنشر الآن.",
      lead:
        "تُشغّل الحمادي واحدة من أكبر القوى العاملة المدربة مسبقاً والجاهزة بالوثائق في المملكة — قابلة للنشر في موقعك خلال ٧٢ ساعة، بإدارة متكاملة.",
      slaTitle: "النشر خلال ٧٢ ساعة",
      slaText: "الإقامة والتأشيرة، النقل، السكن، معدات الوقاية، الزي الموحد والإشراف — كل ذلك مشمول. جهة اتصال واحدة، فاتورة واحدة، ومقاول واحد مسؤول.",
      includes: [
        { label: "إدارة الإقامة والتأشيرة", icon: "shield" },
        { label: "النقل والسكن", icon: "truck" },
        { label: "معدات الوقاية وزي الحمادي", icon: "hardhat" },
        { label: "إشراف في الموقع", icon: "users" },
        { label: "فرق معتمدة في HSE", icon: "check" },
        { label: "ضمان الاستبدال", icon: "refresh" },
      ],
      tradesTitle: "التخصصات المتاحة",
      tradesSubtitle: "فرق مُدققة ومعتمدة وجاهزة للنشر في جميع تخصصات المقاولات.",
      trades: [
        { name: "عمال الإنشاءات والحديد", count: "+٢٢٠", desc: "البنّاؤون، النجارون، حدّادو التسليح، صبّابو الخرسانة، والمنفّذون." },
        { name: "فنيو الكهروميكانيكية", count: "+١٨٠", desc: "تكييف، كهرباء، سباكة، إطفاء حريق، وفنيو التيار الخفيف." },
        { name: "عمال النظافة الصناعية", count: "+٢٠٠", desc: "فرق نظافة المصافي والمصانع والمنشآت العالية." },
        { name: "موظفو الخدمات الأرضية للمطارات", count: "+١٢٠", desc: "نظافة قمرة الطائرات، عمليات الساحة، سائقو معدات الخدمات الأرضية، عمال الشحن." },
        { name: "تشجير ومناظر طبيعية", count: "+٩٠", desc: "أعمال الحجر والري والزراعة والعناية المستمرة." },
        { name: "صيانة وخدمات المرافق", count: "+١١٠", desc: "نظافة، مكافحة حشرات، صيانة كهروميكانيكية، وفرق المكتب الفني." },
        { name: "مشغلو المعدات الثقيلة", count: "+٦٠", desc: "كرين، رافعة شوكية، شيول، بوبكات، ومشغلو نقل ثقيل." },
        { name: "الإشراف وضبط الجودة", count: "+٤٠", desc: "مهندسو موقع، مشرفون، مسؤولو سلامة، ومفتشو جودة." },
      ],
      cta: "اطلب قوى عاملة",
      ctaSecondary: "تواصل مع العمليات",
      callTitle: "خط مباشر · مكتب التعبئة",
    },


    services: {
      eyebrow: "/ ما نقدّمه",
      heading1: "سبعة خطوط خدمية.",
      heading1Accent: "معيار تشغيلي واحد.",
      description:
        "سواء كنا نصب الخرسانة في الدمام، أو ننظف أرضية مصفاة في ينبع، أو ندفع طائرة A380 في جدة — كل مشروع يسير وفق نفس إجراءات التشغيل، وبروتوكولات الصحة والسلامة، وإطار المساءلة.",
      cards: [
        {
          name: "المقاولات العامة",
          summary:
            "أعمال مدنية، وتكامل كهروميكانيكي، وتشطيبات، وتسليم بنظام المفتاح للمشاريع التجارية والصناعية والحكومية.",
          capabilities: ["المدني والإنشائي", "الكهروميكانيكي", "التشطيبات الداخلية", "التسليم الشامل"],
        },
        {
          name: "البناء والإنشاءات",
          summary:
            "إنشاء من الأساس للأبراج والفلل والمصانع والبنية التحتية العامة — بتنفيذ يواكب معايير الجودة والسلامة السعودية.",
          capabilities: ["الخرسانة والحديد", "الأبراج العالية", "الفلل والإسكان", "البنية التحتية"],
        },
        {
          name: "الأعمال الكهروميكانيكية",
          summary:
            "تصميم وتنفيذ وتشغيل كامل للأنظمة الكهروميكانيكية — تكييف، كهرباء، إطفاء حريق، سباكة، وتكامل المباني الذكية.",
          capabilities: ["أنظمة التكييف", "الكهرباء والجهد المنخفض", "إطفاء الحريق", "المباني الذكية"],
        },
        {
          name: "تنسيق الحدائق",
          summary:
            "تنسيق حدائق صلبة وناعمة للمجمعات والحدائق والمقرات المؤسسية — ري وتشجير وصيانة زراعية مستمرة.",
          capabilities: ["الأعمال الصلبة", "أنظمة الري", "التشجير", "العناية الزراعية"],
        },
        {
          name: "خدمات التنظيف",
          summary:
            "تنظيف صناعي وتجاري ومرفقي — من المولات والمستشفيات إلى المصافي ومساكن العمال.",
          capabilities: ["تنظيف يومي", "تنظيف صناعي عميق", "تنظيف الواجهات", "تعقيم"],
        },
        {
          name: "الصيانة",
          summary:
            "صيانة وقائية وتصحيحية لأنظمة التكييف والكهرباء والسباكة وعقود إدارة المرافق في المملكة.",
          capabilities: ["تكييف", "كهرباء", "سباكة", "إدارة مرافق متكاملة"],
        },
        {
          name: "خدمات المطارات الأرضية",
          summary:
            "مناولة المعدات الأرضية، ودوران الطائرات، وخدمات الأمتعة والممرات في أهم البوابات الجوية في المملكة.",
          capabilities: ["خدمات الممرات", "تشغيل المعدات", "مناولة الأمتعة", "دوران الطائرات"],
        },
      ],
    },

    projects: {
      eyebrow: "/ أعمال مختارة",
      heading1: "مشاريع في",
      heading1Accent: "أرجاء المملكة.",
      description:
        "من تشطيبات مقار صندوق الاستثمارات العامة إلى العمليات الأرضية في كبرى المطارات — لمحة عن ما تقدمه فرقنا هذا العام.",
      featured: "مميز",
      loading: "جاري تحميل المشاريع…",
    },

    emGallery: {
      eyebrow: "/ معرض الأعمال الكهروميكانيكية",
      heading1: "كهرباء، تكييف، إطفاء، ذكاء.",
      heading1Accent: "تصميم وتركيب وتشغيل.",
      description:
        "من غرف توزيع الجهد المنخفض إلى محطات التبريد وأنظمة المباني الذكية — لمحة من تنفيذنا الكهروميكانيكي في جميع أرجاء المملكة.",
      photos: [
        { caption: "غرف التكييف المركزية", tag: "تبريد وتهوية" },
        { caption: "توزيع الكهرباء", tag: "لوحات MV / LV" },
        { caption: "التحكم والأتمتة", tag: "BMS / SCADA" },
        { caption: "أنظمة إطفاء الحريق", tag: "رشاشات وإنذار" },
        { caption: "إدارة الكابلات", tag: "حوامل وقنوات" },
        { caption: "الإضاءة والقدرة", tag: "التوصيلات النهائية" },
      ],
    },

    clients: {
      eyebrow: "/ العملاء والشركاء",
      heading1: "ثقة قادة",
      heading1Accent: "في جميع أرجاء المملكة.",
      description:
        "من القطاع المصرفي والطيران السعودي إلى العلامات الصناعية العالمية — تعمل فرق الحمادي داخل منشآت أكثر المؤسسات تطلباً في المنطقة.",
      filterAll: "كل القطاعات",
      filterEnergy: "الطاقة",
      filterAviation: "الطيران",
      filterGovernment: "الحكومي",
      filterFinance: "المصرفي",
      filterIndustrial: "الصناعي",
      filterMega: "المشاريع العملاقة",
      labelSector: "القطاع",
      labelSince: "بداية التعاون",
      labelLocation: "الموقع",
      labelScope: "نطاق العمل",
      labelStatus: "حالة الارتباط",
      statusActive: "ساري",
      statusCompleted: "مكتمل",
      cta: "تفاصيل التعاون",
      closeLabel: "إغلاق",
      stats: [
        { value: "+١٢٠", label: "حساب عميل نشط" },
        { value: "١٧", label: "عاماً من الشراكات" },
        { value: "٪٩٨", label: "نسبة الاحتفاظ بالعملاء" },
        { value: "١١", label: "قطاعاً نخدمه" },
      ],
      directory: [
        {
          id: "aramco",
          name: "أرامكو السعودية",
          mark: "ARC",
          sector: "energy",
          since: "٢٠١١",
          location: "الظهران · المنطقة الشرقية",
          status: "active",
          summary:
            "خدمات النظافة الصناعية والصيانة الفنية ودعم عمليات الإيقاف لعدد من منشآت أرامكو ومجمعاتها السكنية.",
          scope: ["النظافة الصناعية", "صيانة كهروميكانيكية", "توريد العمالة", "خدمات المجمعات"],
          accent: "#0E7C66",
        },
        {
          id: "sabic",
          name: "سابك",
          mark: "SBC",
          sector: "industrial",
          since: "٢٠١٣",
          location: "مدينة الجبيل الصناعية",
          status: "active",
          summary:
            "نظافة المصانع البتروكيماوية وأعمال العزل وتوريد العمالة المتعاقدة لعمليات التشغيل والإيقاف.",
          scope: ["نظافة المصانع", "أعمال العزل", "عمالة ماهرة", "تركيب السقالات"],
          accent: "#1E5BA8",
        },
        {
          id: "neom",
          name: "نيوم",
          mark: "NEO",
          sector: "mega",
          since: "٢٠٢١",
          location: "منطقة تبوك",
          status: "active",
          summary:
            "خدمات الإمداد الميداني والأعمال المدنية ونشر القوى العاملة في مواقع ذا لاين وتروجينا.",
          scope: ["الأعمال المدنية", "الإمداد الميداني", "مخيمات العمالة", "النظافة الثقيلة"],
          accent: "#000000",
        },
        {
          id: "rsg",
          name: "البحر الأحمر العالمية",
          mark: "RSG",
          sector: "mega",
          since: "٢٠٢٢",
          location: "أملج · تبوك",
          status: "active",
          summary:
            "دعم تشطيبات المنتجعات وأعمال التشجير وصيانة المرافق المستمرة في مشاريع الجزر السياحية.",
          scope: ["التشجير", "التشطيبات", "صيانة المرافق", "النظافة"],
          accent: "#B47A3F",
        },
        {
          id: "pif",
          name: "صندوق الاستثمارات العامة",
          mark: "PIF",
          sector: "government",
          since: "٢٠١٩",
          location: "الرياض",
          status: "active",
          summary:
            "مقاول معتمد لمشاريع شركات الصندوق التابعة — أعمال كهروميكانيكية وتشطيبات وعمليات تشغيلية.",
          scope: ["كهروميكانيكية", "التشطيبات", "صيانة المساحات", "الإمداد"],
          accent: "#0B6E4F",
        },
        {
          id: "saudia",
          name: "الخطوط السعودية",
          mark: "SV",
          sector: "aviation",
          since: "٢٠١٤",
          location: "جدة · مطار الملك عبدالعزيز",
          status: "active",
          summary:
            "نظافة قمرة الطائرات وتشغيل معدات الخدمات الأرضية وعمليات الساحة على مدار الساعة.",
          scope: ["نظافة الطائرات", "تشغيل المعدات", "عمليات الساحة", "دعم الشحن"],
          accent: "#0E5494",
        },
        {
          id: "kaia",
          name: "مطار الملك عبدالعزيز الدولي",
          mark: "JED",
          sector: "aviation",
          since: "٢٠١٢",
          location: "جدة",
          status: "active",
          summary:
            "نظافة عميقة للصالات وصيانة فنية وأعمال للواجهات في صالات الحج والركاب الرئيسية.",
          scope: ["نظافة الصالات", "أعمال الواجهات", "صيانة كهروميكانيكية", "مكافحة الحشرات"],
          accent: "#0A3D62",
        },
        {
          id: "rcjy",
          name: "الهيئة الملكية بالجبيل",
          mark: "RCJY",
          sector: "government",
          since: "٢٠١٠",
          location: "مدينة الجبيل الصناعية",
          status: "active",
          summary:
            "أعمال التشجير على مستوى المدينة، ونظافة المناطق العامة، وصيانة البنية التحتية بعقود إطارية.",
          scope: ["التشجير", "نظافة المناطق العامة", "الصيانة", "مكافحة الحشرات"],
          accent: "#2E7D32",
        },
        {
          id: "alrajhi",
          name: "مصرف الراجحي",
          mark: "AR",
          sector: "finance",
          since: "٢٠١٥",
          location: "الرياض · على مستوى المملكة",
          status: "active",
          summary:
            "تشطيب الفروع، وتركيب اللوحات، وخدمات المرافق لأكثر من ٢٠٠ فرع في جميع أنحاء المملكة.",
          scope: ["تشطيب الفروع", "اللوحات", "خدمات المرافق", "الصيانة"],
          accent: "#0E6D3F",
        },
        {
          id: "snb",
          name: "البنك الأهلي السعودي",
          mark: "SNB",
          sector: "finance",
          since: "٢٠١٧",
          location: "الرياض · على مستوى المملكة",
          status: "active",
          summary:
            "صيانة فنية للمقر الرئيسي وتجديدات الفروع وأعمال أكشاك الصراف الآلي بعقود سنوية متجددة.",
          scope: ["صيانة المقر", "التجديدات", "أكشاك الصراف", "النظافة"],
          accent: "#0B5394",
        },
        {
          id: "maaden",
          name: "معادن",
          mark: "MDN",
          sector: "industrial",
          since: "٢٠١٦",
          location: "رأس الخير · المنطقة الشرقية",
          status: "active",
          summary:
            "خدمات نظافة التعدين والمصاهر، وتوريد العمالة، وأعمال العزل في مجمع رأس الخير.",
          scope: ["نظافة المصاهر", "العزل", "العمالة", "السقالات"],
          accent: "#7B341E",
        },
        {
          id: "mod",
          name: "وزارة الدفاع",
          mark: "MOD",
          sector: "government",
          since: "٢٠١٣",
          location: "الرياض · المملكة العربية السعودية",
          status: "active",
          summary:
            "صيانة منشآت معتمدة، وأعمال الصيانة والمساحات، وتوريد عمالة موثوقة لمجمعات وزارة الدفاع.",
          scope: ["خدمات المرافق", "صيانة المساحات", "العمالة", "مكافحة الحشرات"],
          accent: "#3B3F3A",
        },
      ],
    },


    milestones: {
      eyebrow: "/ مسيرتنا",
      heading1: "سبعة عشر عاماً.",
      heading1Accent: "ومسار واحد.",
      description:
        "من رخصة مقاولات واحدة في جدة إلى عمليات متعددة الفروع تخدم أهم مشاريع المملكة.",
      items: [
        { year: "٢٠٠٨", title: "التأسيس في جدة", text: "تم تسجيل شركة الحمادي للمقاولات تحت السجل التجاري ١١١٦٠٠٤٣٢٣ بتركيز على المقاولات العامة." },
        { year: "٢٠١١", title: "أول تعاون مع أرامكو", text: "ترسية أول عقد إطاري مع أرامكو السعودية للنظافة الصناعية وتوريد العمالة." },
        { year: "٢٠١٣", title: "التوسع في المنطقة الشرقية", text: "افتتاح فرع الدمام لخدمة عملاء الجبيل الصناعية بما في ذلك سابك." },
        { year: "٢٠١٦", title: "قوة عمل تتجاوز الألف", text: "تجاوز ١٠٠٠ موظف نشط في الأعمال المدنية والكهروميكانيكية والنظافة والمطارات." },
        { year: "٢٠١٩", title: "مقاول معتمد لدى صندوق الاستثمارات", text: "إدراج المنشأة كمقاول معتمد لدى عدة شركات تابعة لصندوق الاستثمارات العامة." },
        { year: "٢٠٢١", title: "النشر في نيوم", text: "نشر أول الفرق في مواقع ذا لاين وتروجينا بمنطقة تبوك." },
        { year: "٢٠٢٣", title: "أحد عشر موقعاً", text: "العمل من ثمانية فروع داخل المملكة وثلاثة مكاتب توظيف دولية." },
        { year: "٢٠٢٦", title: "شريك رؤية ٢٠٣٠", text: "التوافق مع خارطة طريق السعودة؛ توظيف نطاقات بلاتيني في الهندسة والإشراف." },
      ],
    },

    certifications: {
      eyebrow: "/ عمليات معتمدة",
      heading1: "مدقَّق، معتمد،",
      heading1Accent: "وموثوق.",
      description:
        "تعمل الحمادي داخل أكثر البيئات تنظيماً في المملكة — المصافي والمطارات والوزارات والأبراج المصرفية. كل شهادة نملكها هي تصريح للعمل حيث يهم.",
      items: [
        { code: "ISO 9001", title: "إدارة الجودة" },
        { code: "ISO 14001", title: "الإدارة البيئية" },
        { code: "ISO 45001", title: "الصحة والسلامة المهنية" },
        { code: "NEBOSH", title: "برنامج HSE" },
        { code: "OSHA", title: "سلامة مواقع العمل" },
        { code: "السعودة", title: "نطاقات بلاتيني" },
        { code: "أرامكو", title: "مقاول معتمد" },
        { code: "GACA", title: "عمليات المطارات" },
      ],
    },

    vision2030: {
      eyebrow: "/ رؤية ٢٠٣٠",
      heading1: "نبني المستقبل",
      heading1Accent: "الذي تخيّلته المملكة.",
      description:
        "تُسهم الحمادي مباشرة في أربعة محاور من رؤية ٢٠٣٠ — البنية التحتية، والسعودة، والخدمات الحكومية، وتنفيذ المشاريع العملاقة. عملياتنا في نيوم والبحر الأحمر ومشاريع الصندوق ومنشآت أرامكو تجعلنا شريكاً فاعلاً.",
      pillars: [
        { tag: "البنية التحتية", title: "تنفيذ المشاريع العملاقة", text: "أعمال مدنية وكهروميكانيكية ومخيمات عمالة لمشاريع نيوم والبحر الأحمر وصندوق الاستثمارات." },
        { tag: "السعودة", title: "نطاقات بلاتيني", text: "توظيف نشط للسعوديين في الهندسة والإشراف والعمليات." },
        { tag: "الخدمات العامة", title: "عقود حكومية", text: "اتفاقيات إطارية مستمرة مع الهيئة الملكية ووزارة الدفاع وعملاء وزاريين." },
        { tag: "الصناعة", title: "الطاقة والصناعة", text: "نظافة المصانع ودعم عمليات الإيقاف والصيانة لأرامكو وسابك ومعادن." },
      ],
      cta: "تواصل مع الحمادي",
    },

    testimonials: {
      eyebrow: "/ ماذا يقول العملاء",
      heading1: "موثوق في الميدان.",
      heading1Accent: "وموصى به في القمة.",
      description:
        "آراء مختارة من مديري العمليات ومديري المرافق ومسؤولي المشتريات في أنحاء المملكة.",
      items: [
        { quote: "تم نشر فرق الحمادي في نافذة الإيقاف الخاصة بنا في أقل من ٧٢ ساعة. الانضباط في الموقع هو بالضبط ما تحتاجه منشأة أرامكو.", name: "مدير مرافق", company: "قطاع الطاقة · المنطقة الشرقية" },
        { quote: "تأتي العمالة بالزي الرسمي، وفي الوقت المحدد، ومع معدات الوقاية. هذا وحده يضعهم في مقدمة معظم المقاولين الذين ندقّقهم.", name: "مدير مشتريات", company: "بتروكيماويات · الجبيل" },
        { quote: "نفذنا مع الحمادي ثلاث دورات تجديد فروع. متوقعون، تسليم نظيف، وبلا انزلاقات في قوائم الإصلاحات.", name: "مدير مرافق", company: "مصرفي · الرياض" },
        { quote: "تتطلب عمليات الساحة الجوية دقة. تتكامل فرق الحمادي مع إجراءاتنا التشغيلية بسلاسة.", name: "مدير عمليات", company: "طيران · جدة" },
        { quote: "ما أقدّره أكثر هو الاتساق — نفس المعيار في تبوك كما في الجبيل. وهذا نادر في هذا السوق.", name: "مدير مشروع", company: "مشروع عملاق · تبوك" },
      ],
    },

    branches: {
      eyebrow: "/ حضورنا",
      heading1: "شركة واحدة.",
      heading1Accent: "أحد عشر موقعاً.",
      description:
        "ثمانية فروع تشغيلية في أرجاء المملكة وثلاثة مكاتب توظيف دولية — تمنح الحمادي الانتشار، وسلسلة إمداد القوى العاملة، وسرعة النشر التي تتطلبها أكثر من ١٠٠٠ عقد نشط.",
      ksaTitle: "شبكة المملكة",
      ksaSubtitle: "فروع تشغيلية في المملكة العربية السعودية",
      intlTitle: "المكاتب الدولية",
      intlSubtitle: "شركاء التوظيف ومصادر القوى العاملة",
      labelHQ: "الفرع الرئيسي",
      labelBranch: "فرع إقليمي",
      labelIntl: "مكتب توظيف",
      ksa: [
        { city: "جدة", region: "منطقة مكة المكرمة", role: "headquarters", note: "المقر الرئيسي للمجموعة" },
        { city: "الرياض", region: "منطقة الرياض", role: "branch", note: "العمليات المركزية والحسابات الحكومية" },
        { city: "الدمام", region: "المنطقة الشرقية", role: "branch", note: "المشاريع الصناعية وقطاع النفط" },
        { city: "المدينة المنورة", region: "منطقة المدينة المنورة", role: "branch", note: "خدمة المرافق والمواقع الدينية" },
        { city: "أبها", region: "منطقة عسير", role: "branch", note: "عمليات المرتفعات الجنوبية" },
        { city: "بريدة", region: "منطقة القصيم", role: "branch", note: "تغطية وسط المملكة" },
        { city: "تبوك", region: "منطقة تبوك", role: "branch", note: "مشاريع نيوم والشمال الغربي" },
        { city: "حائل", region: "منطقة حائل", role: "branch", note: "محور الخدمات اللوجستية الشمالية" },
        { city: "جازان", region: "منطقة جازان", role: "branch", note: "بوابة البحر الأحمر الجنوبية" },
      ],
      intl: [
        { country: "بنغلاديش", city: "دكا", note: "توظيف العمالة الماهرة والتدريب قبل الإيفاد" },
        { country: "الهند", city: "نيودلهي", note: "توظيف الكوادر الفنية وتنظيم التعبئة" },
        { country: "نيبال", city: "كاتماندو", note: "سلسلة عمالة معتمدة مع الفحوصات الطبية" },
      ],
    },

    contact: {
      eyebrow: "/ تواصل مع الحمادي",
      heading1: "اطلب خدمة.",
      heading1Accent: "واستلم اتصالاً.",
      description:
        "أخبرنا عن موقعك ونطاق العمل والجدول الزمني. سيقوم مدير حساب كبير بالرد خلال يوم عمل واحد بعرض مخصص.",
      addressTitle: "المقر المسجل",
      addressLine1: "حي الملك فهد، شارع الأمير مشهور بن عبدالعزيز،",
      addressLine2: "مبنى ٥٢٠٧، الدوادمي ١٧٤٥٢، المملكة العربية السعودية",
      phoneTitle: "الهاتف",
      emailTitle: "البريد الإلكتروني",
      websiteTitle: "الموقع الإلكتروني",
      labelName: "الاسم الكامل",
      labelEmail: "البريد الإلكتروني",
      labelPhone: "رقم الهاتف",
      labelService: "الخدمة المطلوبة",
      labelMessage: "ملخص المشروع",
      placeholderName: "محمد الحمادي",
      placeholderEmail: "you@company.com",
      placeholderPhone: "+966 …",
      placeholderMessage: "النطاق، الموقع، الجدول الزمني…",
      selectService: "اختر خدمة",
      serviceOptions: [
        "المقاولات العامة",
        "البناء والإنشاءات",
        "الأعمال الكهروميكانيكية",
        "تنسيق الحدائق",
        "خدمات التنظيف",
        "الصيانة",
        "خدمات المطارات الأرضية",
      ],
      disclaimer:
        "بإرسالك للنموذج، فإنك توافق على التواصل معك من قبل ممثلي شركة الحمادي للمقاولات.",
      submit: "إرسال الطلب",
      submitting: "جارٍ الإرسال…",
      toastFill: "يرجى تعبئة جميع الحقول قبل الإرسال.",
      toastSuccess: "تم إرسال طلبك. سيتواصل معك فريقنا خلال ٢٤ ساعة.",
      toastError: "تعذر الإرسال. حاول مرة أخرى.",
    },

    footer: {
      tagline:
        "شركة محمد ناصر الحمادي للمقاولات — مقرها المملكة العربية السعودية، متوائمة مع رؤية ٢٠٣٠، تقدم خدمات المقاولات والتنظيف والصيانة والخدمات الأرضية للمطارات على أوسع نطاق.",
      servicesTitle: "الخدمات",
      companyTitle: "الشركة",
      reachTitle: "التواصل",
      links: {
        about: "عن الشركة",
        projects: "المشاريع",
        contact: "تواصل",
        admin: "لوحة التحكم",
      },
      services: ["المقاولات", "البناء", "الكهروميكانيكي", "تنسيق الحدائق", "التنظيف", "الصيانة", "خدمات المطارات"],
      addressLine1: "حي الملك فهد، شارع الأمير مشهور بن عبدالعزيز،",
      addressLine2: "مبنى ٥٢٠٧، الدوادمي ١٧٤٥٢، السعودية",
      copyright: "جميع الحقوق محفوظة.",
      madeIn: "صُنع في المملكة العربية السعودية",
    },

    login: {
      eyebrow: "/ لوحة المسؤول",
      heading1: "تسجيل الدخول إلى",
      heading1Accent: "عمليات الحمادي.",
      description:
        "إدارة المشاريع، المخزون، الموظفين، وطلبات الخدمة الواردة في جميع أنحاء المملكة.",
      labelEmail: "البريد الإلكتروني",
      labelPassword: "كلمة المرور",
      submit: "تسجيل الدخول",
      submitting: "جارٍ تسجيل الدخول…",
      demoLabel: "بيانات تجريبية:",
      right: {
        eyebrow: "رؤية ٢٠٣٠ · المملكة",
        h1: "بُني بأيدي",
        accent: "ألف",
        h2: "عامل.",
        description:
          "من مدارج جدة إلى أبراج الرياض — تنسق شركة الحمادي للمقاولات العمليات التي تدعم أكبر مشاريع المملكة العربية السعودية.",
      },
    },

    gallery: {
      eyebrow: "/ معرض الصور",
      heading1: "أرشيف الحمادي البصري.",
      heading1Accent: "فريق. مواقع. أنظمة. قصص.",
      description:
        "كل زي موحد ومشروع ولحظة فريق — موثقة في جميع أرجاء المملكة وما بعدها.",
      backHome: "العودة للرئيسية",
      photoCount: "صورة",
      filters: {
        all: "الكل",
        workforce: "فريق العمل",
        services: "الخدمات",
        em: "كهروميكانيكي",
        projects: "المشاريع",
        culture: "الحياة في الحمادي",
      },
    },
  },
};

export default dictionary;
